import { UNRESOLVED_ENTITY_REF_LABEL, type EntityRefType } from '@orn/global/assistant/inlineMarkup.js';

import toCurrency from '../../utils/money/toCurrency';
import { billDisplayName } from '../bill/billDisplayName';

import type { IBillDisplayNameSource } from '../bill/billDisplayName';

/**
 * The fields a label reads off a resolved row. Structural rather than the
 * chip's generated fragment union, so this module stays free of `__generated__`
 * imports and testable without a GraphQL client.
 */
export type EntityChipLabelTarget =
  | { __typename: 'Merchant'; name: string }
  | { __typename: 'FinancialAccount'; name: string; mask?: string | null }
  | { __typename: 'Transaction'; amount: string }
  | ({ __typename: 'Bill' } & IBillDisplayNameSource)
  | { __typename: 'RecurringTransaction'; description: string; merchant?: { name: string } | null };

/**
 * The text a chip renders for a resolved row, or the per-type fallback when the
 * id resolved to nothing.
 *
 * Derived here, never authored by the model: the assistant emits only
 * `<merchant_id=… />` and the client resolves it, so a chip cannot name one
 * merchant while the underlying row is another.
 *
 * These renderings are quoted verbatim in `@orn/core`'s `lib/assistantMessage/enrichedMessage.ts`
 * so the model knows what it must not restate. Nothing types that link — changing
 * a label here means changing the prompt too. The same goes for the plain-text
 * twin: `@orn/api`'s `lib/async/assistant/renderInlineMarkupInReply.ts` derives
 * these labels by hand for SMS and email, where no chip is drawn. Only the
 * fallback words are shared — {@link UNRESOLVED_ENTITY_REF_LABEL}, beside the
 * grammar in `@orn/global` — since a leaf package can hold a constant but not
 * the GraphQL rows this reads.
 *
 * @param refType - the record type the token named; picks the fallback.
 * @param target - the resolved row, or `null` when the id did not resolve.
 */
export function entityChipLabel(refType: EntityRefType, target: EntityChipLabelTarget | null): string {
  if (target === null) {
    return UNRESOLVED_ENTITY_REF_LABEL[refType];
  }
  switch (target.__typename) {
    case 'Merchant':
      return target.name;
    case 'FinancialAccount':
      // Matches how every other alert surface names an account — see
      // `utils/alert/alertRow.ts`, which builds the same `name ••mask` suffix.
      return target.mask ? `${target.name} ••${target.mask}` : target.name;
    case 'Transaction': {
      const amount = Number(target.amount);
      // Bare: the chip draws a currency glyph, so `toCurrency`'s own `$` beside
      // it would read "$ $75.15". Still signed the way every other transaction
      // surface signs it — see `tables/TransactionsTable` and
      // `panes/transactions/TransactionPane`, which a chip click opens.
      return toCurrency(Math.abs(amount), { showSign: amount < 0, symbol: false });
    }
    // Both coalesce merchant-first because the surfaces a click opens do —
    // `BillPane` names its hero with the same three-way coalesce, the recurring
    // stream page's hero reads `merchant?.name ?? description` — and a chip
    // must agree with the surface it opens.
    case 'Bill':
      return billDisplayName(target);
    case 'RecurringTransaction':
      return target.merchant?.name ?? target.description;
    default: {
      // A target type without a label of its own falls back to saying what it
      // is rather than rendering an empty pill.
      const _exhaustive: never = target;
      void _exhaustive;
      return UNRESOLVED_ENTITY_REF_LABEL[refType];
    }
  }
}
